/**
 * UserMenu Component
 *
 * Dropdown menu for user profile and logout
 */

import styled from '@emotion/styled'
import { useKeycloak } from '@lib/keycloak'
import { useCurrentUser } from '@shared/hooks'
import { getUserInitials } from '@shared/utils'
import { useContext } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import ProfileMenu from '@hrworks/sui-extension/ProfileMenu'
import { DropdownContext } from '@hrworks/sui-shared/components/Dropdown/DropdownContext'

import { useI18n } from '../../../contexts'

const Avatar = styled.div<{ size: number; inverted?: boolean }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  background-color: ${props =>
    props.inverted
      ? props.theme.sqwTier2Color.text.inverse
      : props.theme.sqwTier2Color.background.brand.bold.default};
  color: ${props =>
    props.inverted
      ? props.theme.sqwTier2Color.background.brand.bold.default
      : props.theme.sqwTier2Color.text.inverse};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.size * 0.4}px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
`

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  color: ${props => props.theme.colors.text.primary};

  &:hover {
    background-color: ${props => props.theme.sqwTier2Color.background.nav.hovered};
  }

  &:active {
    background-color: ${props => props.theme.colors.background.default};
  }
`

const LanguageSwitcher = styled.div`
  width: 100%;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props => props.theme.colors.text.primary};
  font-size: 14px;
`

const LanguageLabel = styled.span`
  flex: 1;
`

const LanguageFlags = styled.div`
  display: flex;
  gap: 12px;
`

const FlagButton = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  padding: 0;
  opacity: ${props => props.active ? 1 : 0.4};
  transition: opacity 0.2s;

  &:hover {
    opacity: ${props => props.active ? 1 : 0.7};
  }
`

const MenuContainer = styled.div`
  margin: -20px;
`

const MenuContent = () => {
  const navigate = useNavigate()
  const { logout } = useKeycloak()
  const { formatMessage } = useIntl()
  const { locale, changeLocale } = useI18n()
  const { setIsOpen } = useContext(DropdownContext)

  const handleProfileClick = () => {
    setIsOpen(false)
    void navigate('/profile')
  }

  const handleThemeClick = () => {
    setIsOpen(false)
    void navigate('/theme')
  }

  const handleLogoutClick = () => {
    logout()
  }

  const handleLanguageChange = (newLocale: 'en' | 'de') => {
    changeLocale(newLocale)
  }

  return (
    <MenuContainer>
      <MenuItem onClick={handleProfileClick}>
        {formatMessage({ id: 'userMenu.myProfile' })}
      </MenuItem>
      <MenuItem onClick={handleThemeClick}>
        {formatMessage({ id: 'userMenu.theme' })}
      </MenuItem>
      <LanguageSwitcher>
        <LanguageLabel>{formatMessage({ id: 'userMenu.language' })}</LanguageLabel>
        <LanguageFlags>
          <FlagButton
            active={locale === 'en'}
            onClick={() => handleLanguageChange('en')}
            title="English"
          >
            🇬🇧
          </FlagButton>
          <FlagButton
            active={locale === 'de'}
            onClick={() => handleLanguageChange('de')}
            title="Deutsch"
          >
            🇩🇪
          </FlagButton>
        </LanguageFlags>
      </LanguageSwitcher>
      <MenuItem onClick={handleLogoutClick}>
        {formatMessage({ id: 'userMenu.logout' })}
      </MenuItem>
    </MenuContainer>
  )
}

export const UserMenu = () => {
  const user = useCurrentUser()

  if (!user) {
    return null
  }

  const initials = getUserInitials(user.name)

  return (
    <ProfileMenu
      headerChildren=""
      placement="bottom-end"
      portrait={<Avatar size={70} inverted>{initials}</Avatar>}
      subtitle={user.email}
      title={user.name}
      trigger={<Avatar size={46}>{initials}</Avatar>}
    >
      <MenuContent />
    </ProfileMenu>
  )
}
